import React, { useCallback, useMemo } from 'react';
import type { ActionMeta, CSSObjectWithLabel, GroupBase, MenuListProps, MultiValue } from 'react-select';
import Select, { components } from 'react-select';
import { OptionWithCheckBoxRight } from '../Selector/components/Options/OptionWithCheckBoxRight';
import { SelectorStyles, selectorErrorMessage, selectorLabel, selectorWrapper } from '../Selector/styles';
import { SelectorOptionTypes } from '../Selector/types';
import { getErrorMessage } from '../utils/getErrorMessage';
import { CustomControl } from './components/CustomControl';
import { CustomMultiValue } from './components/CustomMultiValue';
import { CustomOption } from './components/CustomOption';
import { SelectAllMenuList } from './components/SelectAllMenuList';
import { SummaryValueContainer } from './components/SummaryValueContainer';
import type { MultiSelectProps, OptionType } from './types';
import ChevronDown from '/public/icons/chevron-down.svg';

export function MultiSelect<T extends OptionType>({
  options,
  onChange,
  startIcon,
  errors,
  name,
  label,
  required,
  setSearchValue,
  hasNextPage,
  optionType = SelectorOptionTypes.DEFAULT,
  isFetchingNextPage,
  isLoading,
  fetchNextPage,
  summaryLabel,
  withSelectAll,
  ...props
}: MultiSelectProps<T>) {
  const errorMessage = getErrorMessage(errors, name ?? '');

  const handleChange = useCallback(
    (value: MultiValue<T>, actionMeta: ActionMeta<T>) => {
      onChange?.(value, actionMeta);
    },
    [onChange]
  );

  const onMenuScrollToBottom = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) {
      void fetchNextPage?.();
    }
  }, [fetchNextPage, hasNextPage, isFetchingNextPage]);

  const onInputChange = (val: string) => {
    setSearchValue?.(val);
  };

  const controlStyles = useMemo(
    () =>
      SelectorStyles({
        invalid: Boolean(errorMessage),
        customStyles: {
          ...props.customStyles,
          control: (base: CSSObjectWithLabel, state) => {
            const controlFn = SelectorStyles({
              invalid: Boolean(errorMessage),
              customStyles: props.customStyles,
            })?.control;
            const updatedBase = controlFn ? controlFn(base, state) : base;
            const isSearchable = props.isSearchable ?? true;
            return {
              ...updatedBase,
              padding: startIcon ? '3px 12px 3px 36px' : '3px 12px 3px 2px',
              ...(isSearchable === false && { minHeight: '44px' }),
            };
          },
          input: (base: CSSObjectWithLabel) => {
            const isSearchable = props.isSearchable ?? true;
            return {
              ...base,
              ...(isSearchable === false && { height: 0, minHeight: 0, visibility: 'hidden', padding: 0, margin: 0 }),
            };
          },
          ...(summaryLabel
            ? {
                valueContainer: (base: CSSObjectWithLabel) => ({
                  ...base,
                  flexWrap: 'nowrap' as const,
                  overflow: 'hidden',
                  padding: '2px 8px',
                }),
              }
            : {}),
        },
      }),

    [props.customStyles, startIcon, errorMessage, props.isSearchable, summaryLabel]
  );

  const MenuList = useCallback((props: MenuListProps<T>) => {
    const isLoadingMore = props.selectProps.isLoading;
    return (
      <components.MenuList {...props}>
        {props.children}
        {isLoadingMore && <p className="text-body-regular text-grey-700 text-center py-1">Loading...</p>}
      </components.MenuList>
    );
  }, []);

  const DropdownIndicator = useCallback(
    (props: { selectProps: { menuIsOpen: boolean } }) => (
      <ChevronDown
        className={`transition-transform duration-200 text-black fill-current w-4 h-4 ${
          props.selectProps.menuIsOpen ? 'rotate-180' : ''
        }`}
      />
    ),
    []
  );

  const componentsMemo = useMemo(
    () => ({
      MenuList: withSelectAll ? SelectAllMenuList : MenuList,
      Option: optionType === 'check-box-right' ? OptionWithCheckBoxRight : CustomOption,
      MultiValue: CustomMultiValue,
      ...(summaryLabel
        ? {
            ValueContainer: SummaryValueContainer,
          }
        : {}),
      DropdownIndicator,
      LoadingMessage: () => null,
      Control: CustomControl,
      NoOptionsMessage: () => <p className="text-body-regular text-grey-700 text-center py-1">No options</p>,
    }),
    [MenuList, DropdownIndicator, optionType, summaryLabel, withSelectAll]
  );

  return (
    <div className={selectorWrapper()}>
      {label && <p className={selectorLabel({ required })}>{label}</p>}
      <div className="relative">
        {startIcon && <div className="absolute left-3 bottom-3 z-[2]">{startIcon}</div>}
        <Select<T, true, GroupBase<T>>
          options={options}
          isMulti
          closeMenuOnSelect={false}
          hideSelectedOptions={false}
          onMenuScrollToBottom={onMenuScrollToBottom}
          onInputChange={onInputChange}
          components={componentsMemo}
          className="w-full"
          styles={controlStyles}
          onChange={handleChange}
          isLoading={isFetchingNextPage || isLoading}
          {...props}
          {...(summaryLabel ? ({ summaryLabel } as object) : {})}
        />
      </div>
      {errorMessage && <p className={selectorErrorMessage()}>{errorMessage}</p>}
    </div>
  );
}
